import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TradingChart from "../TradingChart";
import WatchlistSidebar from "./WatchlistSidebar";
import OrderTicket from "./OrderTicket";
import PaperDock from "./PaperDock";
import ClosePositionModal from "./ClosePositionModal";
import useOkxTickers from "../../hooks/useOkxTickers";
import usePaperEngine from "../../hooks/usePaperEngine";
import useEngineAccounts from "../../hooks/useEngineAccounts";
import { getLeverage, unrealizedPnl, ORDER_SIDE, ROLE } from "../../lib/paper/engine";

const fmt = (p) => (p == null || Number.isNaN(p) ? "—" : p.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const usd = (v) => (v == null || Number.isNaN(v) ? "—" : `${v < 0 ? "−" : "+"}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

// TradingView-style manual paper-trading workspace: chart + quick trade boxes
// and floating ticket (left, with the account dock below), watchlist (right).
// Manual accounts are client-side sims persisted to the backend
// (routes/manual_paper.py); they share nothing with the autonomous-engine
// paper accounts, which can be selected here but are strictly view-only.
const VIEW_KEY = "aeon.paper.view.v1"; // engine account id being viewed, or ""
const loadView = () => { try { return localStorage.getItem(VIEW_KEY) || null; } catch { return null; } };
// Optional layout props (Markets › Live Desk):
//   rail({ watchlist })  → replaces the right watchlist column with a custom rail
//                          (the watchlist element is handed back to be placed in it)
//   railWidth, chartHeight, markSymbols (dot open-position symbols), liveHeader
export default function PaperWorkspace({
  symbol: symbolProp, onSymbolChange, chartPositions,
  rail = null, railWidth = 360, chartHeight = 440, markSymbols = [], liveHeader = false,
} = {}) {
  // Symbol can be driven externally (e.g. the Live Market Execution instrument
  // tabs) or managed internally (standalone use). Falls back to internal state.
  const [internalSel, setInternalSel] = useState("BTC");
  const selected = symbolProp != null ? symbolProp : internalSel;
  const setSelected = onSymbolChange || setInternalSel;
  const [feedQuotes, setFeedQuotes] = useState({});
  const [ticket, setTicket] = useState(null); // { side } | null
  const [closing, setClosing] = useState(null); // position | null
  const [toasts, setToasts] = useState([]);

  const engine = usePaperEngine(feedQuotes);
  const { account, rollup, settings, actions, status } = engine;
  const engineAccts = useEngineAccounts();
  const [viewEngineId, setViewEngineId] = useState(loadView);

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, viewEngineId || ""); } catch {}
  }, [viewEngineId]);

  // Resolve the viewed engine account; a retired/unknown id falls back to manual.
  const engineView = useMemo(() => {
    if (!viewEngineId) return null;
    const summary = engineAccts.accounts.find((a) => a.account_id === viewEngineId);
    if (!summary) return null;
    return { summary, positions: engineAccts.positions.filter((p) => p.account_id === viewEngineId) };
  }, [viewEngineId, engineAccts.accounts, engineAccts.positions]);
  const viewPending = !!viewEngineId && !engineAccts.loaded; // don't flash the manual account first
  const readOnly = !!engineView || viewPending;
  const dockEngineView = engineView || (viewPending
    ? { summary: { account_id: viewEngineId, name: "Loading…", emoji: "" }, positions: [] }
    : null);

  useEffect(() => {
    if (viewEngineId && engineAccts.loaded && !engineAccts.error && !engineView) setViewEngineId(null);
  }, [viewEngineId, engineAccts.loaded, engineAccts.error, engineView]);

  // In view mode the chart shows only that account's positions on the symbol.
  const shownChartPositions = useMemo(() => {
    if (!engineView) return chartPositions || [];
    return engineView.positions
      .filter((p) => (p.symbol || "").replace("/USDT", "") === selected)
      .map((p) => ({
        symbol: p.symbol,
        entry_price: p.entry_price,
        stop_price: p.stop_loss ?? p.stop_price,
        target_price: p.take_profit ?? p.target_price,
        liquidation_price: p.liquidation_price,
        direction: p.direction,
      }));
  }, [engineView, chartPositions, selected]);

  const posSyms = account ? Object.keys(account.positions) : [];
  const ordSyms = account ? Object.values(account.orders).filter((o) => o.status === "working").map((o) => o.symbol) : [];
  const subSyms = useMemo(
    () => [...new Set([selected, ...posSyms, ...ordSyms])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, posSyms.join(","), ordSyms.join(",")]
  );
  const { quotes: ownQuotes } = useOkxTickers(subSyms);

  useEffect(() => {
    if (Object.keys(ownQuotes).length) setFeedQuotes((prev) => ({ ...prev, ...ownQuotes }));
  }, [ownQuotes]);
  const onSidebarQuotes = useCallback((q) => setFeedQuotes((prev) => ({ ...prev, ...q })), []);

  const quote = feedQuotes[selected];
  const leverage = account ? getLeverage(account, selected, settings) : settings.defaultLeverage;

  const pushToast = (msg) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [{ id, msg }, ...t].slice(0, 4));
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  };

  const submitOrder = (spec) => {
    const res = actions.placeOrder(spec);
    if (res?.error) return res;
    const q = feedQuotes[spec.symbol];
    const px = spec.type === "market" ? (spec.side === "buy" ? q?.ask : q?.bid) : spec.limitPrice || spec.stopPrice;
    pushToast(`${spec.type === "market" ? "Market" : "Order"} executed on OKX:${spec.symbol}USDT.P — ${spec.side === "buy" ? "Buy" : "Sell"} ${spec.qty.toFixed(4)} at ${fmt(px)}`);
    return res;
  };

  const confirmClose = (qty) => {
    if (!closing) return;
    actions.closePosition(closing.symbol, qty != null ? { qty } : undefined);
    pushToast(`Closed ${qty != null ? qty.toFixed(4) : "full"} OKX:${closing.symbol}USDT.P`);
    setClosing(null);
  };

  const addAccount = async (opts) => {
    const acct = await actions.addAccount(opts);
    setViewEngineId(null);
    pushToast(`Created account “${acct.name}”`);
  };
  const resetAccount = (id) => {
    if (window.confirm("Reset this paper account to its starting balance? All positions/orders will be cleared.")) actions.resetAccount(id);
  };
  const deleteAccount = async (id) => {
    const name = engine.state?.accounts[id]?.name;
    if (!window.confirm(`Delete “${name}” permanently? Its positions and history are removed on every device.`)) return;
    try {
      await actions.deleteAccount(id);
      pushToast(`Deleted “${name}”`);
    } catch (e) {
      pushToast(e?.message || "Couldn't delete the account");
    }
  };
  const selectManual = (id) => {
    setViewEngineId(null);
    actions.switchAccount(id);
  };
  const selectEngine = (id) => {
    setViewEngineId(id);
    setTicket(null);
    setClosing(null);
  };

  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "SELECT") return;
      if (readOnlyRef.current) return;
      if (e.shiftKey && (e.key === "B" || e.key === "b")) setTicket({ side: ORDER_SIDE.BUY });
      if (e.shiftKey && (e.key === "S" || e.key === "s")) setTicket({ side: ORDER_SIDE.SELL });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const spread = quote?.bid != null && quote?.ask != null ? (quote.ask - quote.bid).toFixed(1) : "—";

  // ── on-chart draggable trade lines for the selected symbol ──
  const tradeOverlay = useMemo(() => {
    if (!account || readOnly) return null;
    const pos = account.positions[selected];
    const last = feedQuotes[selected]?.last;
    const lines = [];
    if (pos && pos.qty > 0) {
      const uPnl = unrealizedPnl(pos, last ?? pos.avgPrice);
      lines.push({
        id: "entry", price: pos.avgPrice, color: "#3b82f6",
        label: `${pos.side === "long" ? "LONG" : "SHORT"} ${pos.qty.toFixed(4)} · ${usd(uPnl)}`,
        onCancel: () => setClosing(pos),
      });
    }
    const dir = pos ? (pos.side === "long" ? 1 : -1) : 1;
    for (const o of Object.values(account.orders)) {
      if (o.symbol !== selected || o.status !== "working") continue;
      if (o.role === ROLE.TP) {
        const pnl = pos ? dir * (o.limitPrice - pos.avgPrice) * pos.qty : null;
        lines.push({ id: o.id, price: o.limitPrice, color: "#22ab94", dashed: true, draggable: true, field: "limitPrice",
          label: `TP ${o.qty.toFixed(4)}${pnl != null ? ` · ${usd(pnl)}` : ""}`, onCancel: () => actions.cancelOrder(o.id) });
      } else if (o.role === ROLE.SL) {
        const pnl = pos ? dir * (o.stopPrice - pos.avgPrice) * pos.qty : null;
        lines.push({ id: o.id, price: o.stopPrice, color: "#f23645", dashed: true, draggable: true, field: "stopPrice",
          label: `SL ${o.qty.toFixed(4)}${pnl != null ? ` · ${usd(pnl)}` : ""}`, onCancel: () => actions.cancelOrder(o.id) });
      } else {
        const px = o.limitPrice ?? o.stopPrice;
        lines.push({ id: o.id, price: px, color: "#eab308", dashed: true, draggable: true, field: o.type === "limit" ? "limitPrice" : "stopPrice",
          label: `${o.type.toUpperCase()} ${o.side} ${o.qty.toFixed(4)}`, onCancel: () => actions.cancelOrder(o.id) });
      }
    }
    if (!lines.length) return null;
    const patchFor = (id, price) => {
      const ln = lines.find((l) => l.id === id);
      return ln?.field ? { [ln.field]: price } : null;
    };
    return {
      lines,
      onDragMove: (id, price) => { const p = patchFor(id, price); if (p) actions.modifyOrder(id, p); },
      onDragEnd: (id, price) => {
        const p = patchFor(id, price); if (!p) return;
        actions.modifyOrder(id, p);
        const ln = lines.find((l) => l.id === id);
        pushToast(`${ln?.field === "limitPrice" && ln.color === "#22ab94" ? "Take Profit" : ln?.field === "stopPrice" ? "Stop Loss" : "Order"} order modified — ${fmt(price)}`);
      },
    };
  }, [account, readOnly, selected, feedQuotes, actions]);

  if (!engine.ready) {
    return (
      <div className="border border-zinc-800 rounded-lg bg-zinc-900/30 p-8 text-center text-sm text-zinc-400" style={{ minHeight: 200 }}>
        {status.loading ? "Loading paper accounts…" : (
          <>
            <div className="text-rose-400 mb-2">Couldn't load paper accounts: {status.error}</div>
            <button onClick={() => window.location.reload()} className="px-3 py-1.5 bg-zinc-800 rounded text-zinc-200 hover:bg-zinc-700">Retry</button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      {(status.error || status.conflict) && (
        <div className={`mb-2 px-3 py-1.5 rounded text-xs flex items-center justify-between ${status.error ? "bg-rose-500/10 text-rose-300 border border-rose-500/30" : "bg-amber-500/10 text-amber-200 border border-amber-500/30"}`}>
          <span>{status.error || status.conflict}</span>
          {!status.error && <button onClick={actions.dismissConflict} className="ml-3 text-zinc-400 hover:text-white">✕</button>}
        </div>
      )}
      <div className={`pw-stage flex flex-col md:flex-row gap-0 border border-zinc-800 rounded-lg overflow-hidden bg-zinc-900/30 ${rail ? "has-rail" : ""}`} style={{ minHeight: 420 }}>
        {/* Left: chart + quick trade boxes */}
        <div className="flex-1 min-w-0 p-2 relative">
          {readOnly ? (
          <div className="absolute z-10 top-[92px] left-6 text-[11px] px-2 py-1 rounded bg-zinc-950/80 text-sky-300 border border-sky-500/30 shadow-lg pointer-events-none" style={{ top: 96 }}>
            Viewing {engineView ? `${engineView.summary.emoji} ${engineView.summary.name}` : "AEON account"} — trading disabled
          </div>
          ) : (
          <div className="absolute z-10 left-4 flex items-stretch text-xs rounded overflow-hidden shadow-lg" style={{ top: rail ? 96 : 16 }}>
            <button onClick={() => setTicket({ side: ORDER_SIDE.SELL })} className="bg-rose-600/90 hover:bg-rose-500 px-2 py-1 text-white text-left">
              <div className="text-[11px] uppercase opacity-80">Sell</div>
              <div className="font-mono tabular-nums">{fmt(quote?.bid)}</div>
            </button>
            <div className="bg-zinc-900/90 px-2 flex items-center text-[10px] text-zinc-400">{spread}</div>
            <button onClick={() => setTicket({ side: ORDER_SIDE.BUY })} className="bg-blue-600/90 hover:bg-blue-500 px-2 py-1 text-white text-right">
              <div className="text-[11px] uppercase opacity-80">Buy</div>
              <div className="font-mono tabular-nums">{fmt(quote?.ask)}</div>
            </button>
          </div>
          )}
          <TradingChart key={selected} symbol={selected} positions={shownChartPositions} tradeOverlay={tradeOverlay}
            height={chartHeight} showControls onSymbolChange={setSelected} liveQuote={liveHeader ? quote : null} />
        </div>

        {rail ? (
          <div className="pw-rail flex-shrink-0 border-t md:border-t-0 md:border-l border-zinc-800" style={{ width: railWidth }}>
            {rail({
              watchlist: <WatchlistSidebar selected={selected} onSelect={setSelected} onQuotes={onSidebarQuotes} markSymbols={markSymbols} />,
            })}
          </div>
        ) : (
        /* Right: watchlist + details */
        <div className="w-full md:w-[240px] flex-shrink-0 border-t md:border-t-0 md:border-l border-zinc-800 h-[380px] md:h-auto md:min-h-[420px]">
          <WatchlistSidebar selected={selected} onSelect={setSelected} onQuotes={onSidebarQuotes} markSymbols={markSymbols} />
        </div>
        )}
      </div>

      {/* Bottom dock (under the workspace) */}
      <PaperDock
        engine={engine}
        onEditPosition={(sym) => { setSelected(sym); setTicket({ side: ORDER_SIDE.BUY }); }}
        onClosePosition={(p) => setClosing(p)}
        onAddAccount={addAccount}
        onResetAccount={resetAccount}
        onDeleteAccount={deleteAccount}
        engineAccounts={engineAccts.accounts}
        engineView={dockEngineView}
        onSelectEngine={selectEngine}
        onSelectManual={selectManual}
        onSelectSymbol={setSelected}
      />

      {/* Floating order ticket */}
      {ticket && !readOnly && (
        <OrderTicket
          symbol={selected}
          quote={quote}
          leverage={leverage}
          availableFunds={rollup.availableFunds}
          settings={settings}
          initialSide={ticket.side}
          onSubmit={submitOrder}
          onSetLeverage={(L) => actions.setLeverage(selected, L)}
          onClose={() => setTicket(null)}
        />
      )}

      {closing && !readOnly && <ClosePositionModal position={closing} onClose={() => setClosing(null)} onConfirm={confirmClose} />}

      {/* toasts (bottom-left) — full toast system arrives in step 6 */}
      <div className="fixed bottom-3 left-3 z-50 space-y-2 w-72">
        {toasts.map((t) => (
          <div key={t.id} className="bg-zinc-900 border border-emerald-500/40 rounded px-3 py-2 text-xs text-zinc-200 shadow-lg">
            ✓ {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
